import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Phone, PhoneOff, Play, RotateCw, User, MessageSquare, Award, BookOpen,
  ArrowRight, ChevronLeft, Sparkles, History,
} from "lucide-react";

/**
 * Roleplay practice — Claude plays a randomly generated prospect, you practice
 * the cold-call sales script from the training manual, and get graded at the
 * end.
 *
 * State machine:
 *   idle → active (session running) → scored (session ended, showing grade)
 */
export default function Roleplay() {
  const [phase, setPhase] = useState("idle"); // idle | active | scored
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const scrollerRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/roleplay/sessions");
      setHistory(data.sessions || []);
    } catch (err) {
      // Non-blocking — history is optional. Log so it isn't silently invisible.
      console.debug("Roleplay history load failed:", err?.message || err);
    }
  };
  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  const startSession = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/roleplay/start", { difficulty: "normal" });
      setSession(data.session);
      setMessages(data.session.messages);
      setPhase("active");
      setDraft("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStarting(false);
    }
  };

  const sendTurn = async () => {
    const msg = draft.trim();
    if (!msg || !session || sending) return;
    setSending(true);
    // Optimistic operator turn
    const optimistic = { role: "operator", content: msg, at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    try {
      const { data } = await api.post(`/roleplay/${session.id}/turn`, { message: msg });
      setMessages((m) => [...m, data.prospect]);
    } catch (err) {
      toast.error(formatApiError(err));
      // Roll back optimistic add
      setMessages((m) => m.slice(0, -1));
      setDraft(msg);
    } finally {
      setSending(false);
    }
  };

  const endSession = async () => {
    if (!session || ending) return;
    if (messages.length < 3) {
      toast.error("Send at least one message before ending the call.");
      return;
    }
    setEnding(true);
    try {
      const { data } = await api.post(`/roleplay/${session.id}/end`);
      setSession(data.session);
      setPhase("scored");
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEnding(false);
    }
  };

  const resetToIdle = () => {
    setPhase("idle");
    setSession(null);
    setMessages([]);
    setDraft("");
  };

  const openPastSession = async (sid) => {
    try {
      const { data } = await api.get(`/roleplay/${sid}`);
      setSession(data.session);
      setMessages(data.session.messages || []);
      setPhase(data.session.status === "ended" ? "scored" : "active");
      setShowHistory(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="roleplay-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Roleplay practice · Claude Sonnet 4.6
          </div>
          <h1 className="text-3xl font-bold text-neutral-900">Cold-call rehearsal</h1>
          <p className="text-neutral-600 mt-2 text-sm max-w-2xl">
            The AI plays a random small-business owner — a plumber, a dentist, an
            insurance broker. You practice the sales script. When you hang up, you
            get a 4-axis score and coaching notes on what to fix next time.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Link
            to="/help"
            className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-900 flex items-center gap-1"
            data-testid="roleplay-back-to-help"
          >
            <ChevronLeft className="w-3 h-3" /> Back to manual
          </Link>
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-900 flex items-center gap-1 ml-3"
            data-testid="roleplay-history-btn"
          >
            <History className="w-3 h-3" /> History ({history.length})
          </button>
        </div>
      </div>

      {showHistory && (
        <HistoryPanel history={history} onOpen={openPastSession} onClose={() => setShowHistory(false)} />
      )}

      {phase === "idle" && (
        <IdleView onStart={startSession} starting={starting} lastScore={history[0]?.score} />
      )}

      {phase === "active" && session && (
        <ActiveView
          persona={session.persona}
          messages={messages}
          draft={draft}
          setDraft={setDraft}
          onSend={sendTurn}
          onEnd={endSession}
          sending={sending}
          ending={ending}
          scrollerRef={scrollerRef}
        />
      )}

      {phase === "scored" && session && (
        <ScoredView session={session} messages={messages} onRestart={resetToIdle} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Phase views                                                          */
/* ------------------------------------------------------------------ */

function IdleView({ onStart, starting, lastScore }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="roleplay-idle">
      <div className="bg-white border border-neutral-200 p-8">
        <div className="w-14 h-14 border-2 border-neutral-900 flex items-center justify-center mb-5">
          <Phone className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-3">Ready for a live drill?</h2>
        <p className="text-neutral-600 text-sm leading-relaxed mb-6">
          One click gets you a fresh random prospect. Their industry, city,
          business name, temperament, and primary objection are all rolled at
          call time — no two practice runs are the same.
        </p>
        <ol className="text-sm text-neutral-700 space-y-2 mb-6 list-decimal pl-5">
          <li>Click <strong>Start practice call</strong>. The AI answers the phone in character.</li>
          <li>Type like you're talking — short sentences, natural tone.</li>
          <li>Handle whatever objection they throw. Aim for a clear next step.</li>
          <li>Click <strong>End call &amp; score me</strong> whenever you're ready.</li>
        </ol>
        <button
          onClick={onStart}
          disabled={starting}
          data-testid="roleplay-start-btn"
          className="lf-btn-primary text-sm flex items-center gap-2"
        >
          {starting ? (
            <><RotateCw className="w-4 h-4 animate-spin" /> Dialing…</>
          ) : (
            <><Play className="w-4 h-4" /> Start practice call</>
          )}
        </button>
      </div>

      <div className="bg-neutral-50 border border-neutral-200 p-8">
        <div className="overline mb-3">Your last score</div>
        {lastScore ? (
          <>
            <div className="flex items-baseline gap-3 mb-4">
              <div className="text-5xl font-bold text-neutral-900">{lastScore.overall}</div>
              <div className="text-lg text-neutral-500">/ 10 overall</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-6">
              <MiniStat label="Opener"    v={lastScore.opener} />
              <MiniStat label="Pain"      v={lastScore.pain_naming} />
              <MiniStat label="Objections" v={lastScore.objection_handling} />
              <MiniStat label="Close"     v={lastScore.close} />
            </div>
            {lastScore.feedback?.next_rep && (
              <div className="text-sm border-l-4 border-amber-400 bg-amber-50 px-4 py-3">
                <div className="text-xs font-mono uppercase tracking-widest text-amber-800 mb-1">
                  For your next call
                </div>
                <div className="text-neutral-800">{lastScore.feedback.next_rep}</div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-neutral-500 text-sm mb-4">
              No previous drills yet. Your first score card will appear here.
            </div>
            <Link
              to="/help#19-the-cold-call-sales-script--full-version"
              className="text-sm text-neutral-900 underline hover:text-amber-700 inline-flex items-center gap-1"
              data-testid="roleplay-read-script-link"
            >
              <BookOpen className="w-4 h-4" /> Read the script first
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function ActiveView({ persona, messages, draft, setDraft, onSend, onEnd, sending, ending, scrollerRef }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6" data-testid="roleplay-active">
      {/* Chat */}
      <div className="bg-white border border-neutral-200 flex flex-col" style={{ height: "70vh", minHeight: 500 }}>
        <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-mono uppercase tracking-widest text-xs text-red-700">Live call</span>
          </div>
          <button
            onClick={onEnd}
            disabled={ending}
            data-testid="roleplay-end-btn"
            className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-red-700 flex items-center gap-1.5"
          >
            {ending ? <><RotateCw className="w-3 h-3 animate-spin" /> Scoring…</> : <><PhoneOff className="w-3 h-3" /> End call &amp; score me</>}
          </button>
        </div>

        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="roleplay-transcript">
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} content={m.content} personaName={persona.contact_name} />
          ))}
          {sending && <Bubble role="prospect" content="…" personaName={persona.contact_name} loading />}
        </div>

        <div className="border-t border-neutral-100 p-3 flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
            }}
            placeholder="Type what you'd say next…  (Enter to send, Shift+Enter for newline)"
            rows={2}
            className="lf-input flex-1 resize-none"
            disabled={sending || ending}
            data-testid="roleplay-input"
          />
          <button
            onClick={onSend}
            disabled={!draft.trim() || sending || ending}
            data-testid="roleplay-send-btn"
            className="lf-btn-primary text-sm flex items-center gap-2 self-end"
          >
            Send <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Persona sidebar */}
      <div className="bg-neutral-50 border border-neutral-200 p-5" data-testid="roleplay-persona">
        <div className="overline mb-3">Prospect on the line</div>
        <div className="text-lg font-bold text-neutral-900 mb-1">{persona.contact_full_name}</div>
        <div className="text-sm text-neutral-600 mb-4">
          <div><strong>{persona.business_name}</strong></div>
          <div>{persona.industry_label} · {persona.city}, {persona.state}</div>
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Temperament</div>
        <div className="text-sm text-neutral-800 mb-4 leading-relaxed">{persona.temperament}</div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Services</div>
        <div className="text-sm text-neutral-800 mb-4">{persona.services_hint}</div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Monthly revenue (rough)</div>
        <div className="text-sm text-neutral-800 mb-4">${persona.monthly_revenue.toLocaleString()}</div>
        <div className="border-t border-neutral-200 pt-4 text-xs text-neutral-500">
          <span className="font-mono uppercase tracking-widest">💡 Hint:</span> use their business name early — signals it's not a bulk dial.
        </div>
      </div>
    </div>
  );
}

function ScoredView({ session, messages, onRestart }) {
  const s = session.score || {};
  const persona = session.persona;
  return (
    <div className="space-y-6" data-testid="roleplay-scored">
      <div className="bg-white border border-neutral-200 p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="overline mb-2 flex items-center gap-2">
              <Award className="w-3.5 h-3.5" /> Call graded
            </div>
            <h2 className="text-2xl font-bold text-neutral-900">
              Overall <span className="text-amber-600">{s.overall}</span>/10
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Call with {persona.contact_full_name} of {persona.business_name} · {persona.city}, {persona.state}
            </p>
          </div>
          <button
            onClick={onRestart}
            data-testid="roleplay-restart-btn"
            className="lf-btn-primary text-sm flex items-center gap-2"
          >
            <Phone className="w-4 h-4" /> Practice another call
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <ScoreCard label="Opener" v={s.opener} testId="score-opener" />
          <ScoreCard label="Pain naming" v={s.pain_naming} testId="score-pain" />
          <ScoreCard label="Objection handling" v={s.objection_handling} testId="score-objections" />
          <ScoreCard label="Close" v={s.close} testId="score-close" />
        </div>

        {s.feedback && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-green-200 bg-green-50 p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-green-800 mb-2">What worked</div>
              <ul className="text-sm text-neutral-800 space-y-2 list-disc pl-5">
                {(s.feedback.wins || []).map((w, i) => <li key={`win-${i}-${w.slice(0,20)}`}>{w}</li>)}
              </ul>
            </div>
            <div className="border border-red-200 bg-red-50 p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-2">What to fix</div>
              <ul className="text-sm text-neutral-800 space-y-2 list-disc pl-5">
                {(s.feedback.misses || []).map((m, i) => <li key={`miss-${i}-${m.slice(0,20)}`}>{m}</li>)}
              </ul>
            </div>
          </div>
        )}

        {s.feedback?.next_rep && (
          <div className="mt-5 border-l-4 border-amber-400 bg-amber-50 px-5 py-4">
            <div className="text-xs font-mono uppercase tracking-widest text-amber-800 mb-1">
              For your next call
            </div>
            <div className="text-neutral-900 font-medium">{s.feedback.next_rep}</div>
          </div>
        )}
      </div>

      {/* Transcript replay */}
      <div className="bg-white border border-neutral-200 p-6">
        <div className="overline mb-4 flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5" /> Transcript
        </div>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} content={m.content} personaName={persona.contact_name} muted />
          ))}
        </div>
      </div>
    </div>
  );
}

function HistoryPanel({ history, onOpen, onClose }) {
  return (
    <div className="mb-6 bg-white border border-neutral-200" data-testid="roleplay-history-panel">
      <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
        <div className="overline">Recent practice calls</div>
        <button onClick={onClose} className="text-xs text-neutral-500 hover:text-neutral-900">Close</button>
      </div>
      {history.length === 0 ? (
        <div className="p-6 text-sm text-neutral-500 text-center">No practice calls yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest font-mono text-neutral-500">
            <tr><th className="text-left p-3">When</th><th className="text-left p-3">Prospect</th><th className="text-left p-3">Turns</th><th className="text-right p-3">Score</th><th></th></tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} className="border-t border-neutral-100" data-testid={`roleplay-history-row-${h.id.slice(0, 8)}`}>
                <td className="p-3 font-mono text-xs">{h.created_at.slice(0, 16).replace("T", " ")}</td>
                <td className="p-3">{h.persona?.business_name || "—"}</td>
                <td className="p-3">{h.message_count}</td>
                <td className="p-3 text-right font-bold">{h.score?.overall ?? <span className="text-neutral-300">active</span>}</td>
                <td className="p-3 text-right">
                  <button onClick={() => onOpen(h.id)} className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-900">
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tiny components                                                     */
/* ------------------------------------------------------------------ */

function Bubble({ role, content, personaName, loading = false, muted = false }) {
  const isOp = role === "operator";
  return (
    <div className={`flex ${isOp ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] ${isOp ? "text-right" : "text-left"}`}>
        <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${muted ? "text-neutral-400" : "text-neutral-500"}`}>
          {isOp ? "You" : personaName || "Prospect"}
        </div>
        <div
          className={`inline-block px-4 py-2.5 text-sm whitespace-pre-wrap ${
            isOp
              ? "bg-neutral-900 text-white"
              : "bg-neutral-100 text-neutral-900 border border-neutral-200"
          } ${loading ? "italic opacity-60" : ""}`}
        >
          {content}
        </div>
      </div>
    </div>
  );
}

function ScoreCard({ label, v, testId }) {
  const val = typeof v === "number" ? v : 0;
  const color = val >= 8 ? "green" : val >= 5 ? "amber" : "red";
  const bgs = { green: "bg-green-50 border-green-200 text-green-800", amber: "bg-amber-50 border-amber-200 text-amber-800", red: "bg-red-50 border-red-200 text-red-800" };
  return (
    <div className={`border p-4 ${bgs[color]}`} data-testid={testId}>
      <div className="text-xs font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-3xl font-bold mt-1">{val}<span className="text-base opacity-60">/10</span></div>
    </div>
  );
}

function MiniStat({ label, v }) {
  return (
    <div className="text-center border border-neutral-200 bg-white p-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-lg font-bold">{v ?? "—"}</div>
    </div>
  );
}
